import React, {Fragment} from 'react';
import {useUser} from '../../context/UserContext';
import {
  CaretDown,
  Copy,
  Launch,
  Logout,
  Loop,
  Reset,
} from '@carbon/icons-react';
import {
  Alert,
  Box,
  Button,
  Center,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useClipboard,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {lightFormat} from 'date-fns';
import {Link} from 'react-router-dom';
import {useMembership} from '../../hooks/api/customer';
import {useLogout} from '../../hooks/api/logout';
import {
  useCustomer,
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {useScoringInfo, useRefreshAccounts} from '../../hooks/api/scoringInfo';
import {useIsKeyplayAdmin} from '../../hooks/api/user';
import {CustomerListWithLogin} from './CustomerSelector';
import {useSettings} from '../Settings';

export const AppMenu = ({token}: {token: string}) => {
  const user = useUser();
  const membership = useMembership();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const customer = useCustomer();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const isFreemiumV3Customer = useIsFreemiumV3Customer();

  const toast = useToast();
  const {onCopy} = useClipboard(import.meta.env.DEV ? token : '');
  const {isOpen, onOpen, onClose} = useDisclosure();

  const {data: scoringInfo} = useScoringInfo();
  const logout = useLogout();

  const triggerRescore = useRefreshAccounts();

  const settings = useSettings();

  const internalMenuOptions = [];
  if (isKeyplayAdmin) {
    internalMenuOptions.push([
      <MenuItem as={Link} icon={<Launch />} key="admin" to="/admin">
        Admin Tools
      </MenuItem>,
    ]);

    if (import.meta.env.DEV) {
      internalMenuOptions.push([
        <MenuItem
          key="token"
          as={Link}
          icon={<Copy />}
          onClick={() => {
            onCopy();
            toast({title: 'Token copied!', position: 'bottom-left'});
          }}
        >
          Copy Token
        </MenuItem>,
      ]);
    }
  }

  const internalMenu =
    internalMenuOptions.length > 0 ? (
      <MenuGroup title="Internal">
        {internalMenuOptions}
        <MenuDivider />
      </MenuGroup>
    ) : null;

  const settingsMenuItems = [...settings.account, ...settings.team].map(
    (setting) => (
      <MenuItem
        key={setting.path}
        icon={setting.icon}
        as={Link}
        to={`/settings/${setting.path}`}
      >
        {setting.label}
      </MenuItem>
    )
  );

  const allMenuItems = {
    accountSwitcher: (
      <Fragment key="account-switcher">
        <MenuItem icon={<Loop />} onClick={onOpen}>
          Switch Accounts
        </MenuItem>
        <SwitchAccountModal isOpen={isOpen} onClose={onClose} />
      </Fragment>
    ),
    divider: <MenuDivider />,
    logout: (
      <MenuItem key="logout" icon={<Logout />} onClick={() => logout.mutate()}>
        Log Out
      </MenuItem>
    ),
    refreshData: (
      <MenuItem
        alignItems="start"
        isDisabled={!!scoringInfo?.runningRefresh}
        icon={
          <Box mt="1">
            <Reset />
          </Box>
        }
        onClick={() => triggerRescore.mutate({})}
      >
        <Flex direction="column" gap="1">
          <Box>Refresh Data</Box>
          {scoringInfo?.latestRefresh?.timestamp && (
            <Box>
              <Text fontSize="xs" color="kgray.300">
                Last updated:{' '}
                {lightFormat(
                  scoringInfo.latestRefresh.timestamp,
                  'M/dd h:mm a'
                )}
              </Text>
            </Box>
          )}
        </Flex>
      </MenuItem>
    ),
  };

  const menuItems = (
    <>
      {(membership?.data?.length ?? 0) > 1 && (
        <>
          {allMenuItems.accountSwitcher}
          {allMenuItems.divider}
        </>
      )}
      {!isFreeOrListBuilderCustomer && (
        <>
          {allMenuItems.refreshData}
          {allMenuItems.divider}
        </>
      )}
      {!isFreemiumV3Customer && (
        <Flex
          bg="kpurple.50"
          border="1px solid"
          borderColor="kred.200"
          borderRadius="lg"
          direction="column"
          fontSize="xs"
          gap="2"
          m="3"
          p="3"
        >
          <Box fontWeight="500">Markets are now under Models</Box>
          <Box>
            You can now find account scoring and serviceable markets alongside
            new intent scoring features in the top nav.
          </Box>
        </Flex>
      )}
      {settingsMenuItems}
      {allMenuItems.divider}
      {allMenuItems.logout}
    </>
  );

  return (
    <Flex overflowX="hidden">
      <Menu>
        <MenuButton
          as={Button}
          variant="link"
          rightIcon={
            <Box>
              <CaretDown />
            </Box>
          }
          iconSpacing="2px"
          color="kgray.200"
          _active={{
            color: 'white',
          }}
          _hover={{
            textDecoration: 'none',
            color: 'white',
          }}
          fontWeight="500"
          flex="1"
        >
          <Flex alignItems="center">
            {!isFreeOrListBuilderCustomer && (
              <Text overflowX="hidden" textOverflow="ellipsis">
                {customer.name}
              </Text>
            )}
            <Image src={user.auth0.picture} h={8} borderRadius="25px" ml={3} />
          </Flex>
        </MenuButton>
        <MenuList color="#0A0A0A" maxWidth="250px">
          {internalMenu}
          {menuItems}
        </MenuList>
      </Menu>
    </Flex>
  );
};

const SwitchAccountModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const membership = useMembership({enabled: isOpen});

  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent py={4}>
        <ModalHeader textAlign="center" fontSize="xl">
          Switch Accounts
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={4} px={20} py={0}>
          <Box textAlign="center">
            Select the account you would like to switch to.
          </Box>
          {membership.isPending ? (
            <Center>
              <Spinner size="md" />
            </Center>
          ) : membership.isError ? (
            <Alert status="error">Something went wrong.</Alert>
          ) : (
            <CustomerListWithLogin customers={membership.data} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
